import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
	Box,
	Center,
	Input,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Spinner,
	Stack,
	StyleProps,
	useFormControlContext,
	XOutline,
} from '@bonusly/design-system';

import { fastRandomId } from '../../../utils/crypto';

type SearchableSelectProps<T> = StyleProps & {
	value: T | null;
	onChange: (value: T | null) => void;
	onSearch: (query: string) => Promise<T[]>;
	getOptionKey: (option: T) => string;
	renderTrigger: (value: T) => ReactNode;
	renderOption: (option: T, isHighlighted: boolean) => ReactNode;
	placeholder: string;
	emptyState?: ReactNode;
	isDisabled?: boolean;
	autoFocus?: boolean;
	onBlur?: () => void;
	'aria-label'?: string;
	clearLabel?: string;
};

enum Status {
	Idle,
	Loading,
}

// Runs an async search and applies its result only if no newer call has started in the
// meantime. The previous serialized-queue version had a race here: `flush()` cleared the
// pending queue but did NOT cancel the in-flight `await run()`, so a stale result could
// land *after* a newer one and silently overwrite it. Each call now bumps a monotonic id;
// resolved results compare against the latest id and bail out if they're stale. Network
// requests for stale calls still complete (we don't yet thread an `AbortSignal` through
// `onSearch`) — their results are just dropped.
const createSearchRunner = <T,>(setOptions: (opts: T[]) => void, setStatus: (status: Status) => void, run: (query: string) => Promise<T[]>) => {
	let currentId = 0;

	return {
		run(query: string) {
			const myId = ++currentId;
			setStatus(Status.Loading);
			void (async () => {
				try {
					const result = await run(query);
					if (myId !== currentId) return;
					setOptions(result);
				} catch {
					if (myId !== currentId) return;
					setOptions([]);
				} finally {
					if (myId === currentId) setStatus(Status.Idle);
				}
			})();
		},
		cancel() {
			currentId += 1;
			setStatus(Status.Idle);
		},
	};
};

/**
 * Combobox-style picker. The trigger IS the search input when nothing is selected
 * — users type directly into it (no separate "open me" button, no chevron). When
 * a value is selected, the trigger swaps to the rich `renderTrigger` content with
 * a clear (X) button. Focusing the empty input opens the dropdown and triggers an
 * empty-string search so callers can pre-populate suggested options.
 */
export const SearchableSelect = <T,>({
	value,
	onChange,
	onSearch,
	getOptionKey,
	renderTrigger,
	renderOption,
	placeholder,
	emptyState,
	isDisabled,
	autoFocus,
	onBlur,
	'aria-label': ariaLabel,
	clearLabel,
	...rest
}: SearchableSelectProps<T>) => {
	const form = useFormControlContext() ?? {};
	const idRef = useRef(fastRandomId());
	const inputRef = useRef<HTMLInputElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();
	const handlerRef = useRef(onSearch);
	handlerRef.current = onSearch;

	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [options, setOptions] = useState<T[]>([]);
	const [status, setStatus] = useState<Status>(Status.Idle);
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	// Mirror highlightedIndex / options in refs so back-to-back keyboard events
	// (ArrowDown then Enter in the same tick) read the freshest values rather than
	// stale closures from the previous render.
	const highlightedRef = useRef(-1);
	const optionsRef = useRef<T[]>(options);

	const setHighlighted = (next: number) => {
		highlightedRef.current = next;
		setHighlightedIndex(next);
	};

	optionsRef.current = options;

	const runner = useMemo(
		() =>
			createSearchRunner<T>(
				(opts) => setOptions(opts),
				(s) => setStatus(s),
				(q) => handlerRef.current(q)
			),
		[]
	);

	const optionId = (index: number) => `searchable-option:${idRef.current}-${index}`;
	const listId = `searchable-list:${idRef.current}`;
	const menuId = `searchable-menu:${idRef.current}`;

	// On open, kick off a single search using the current text so the dropdown shows
	// pre-populated options immediately. Text edits don't go through this effect —
	// `handleInputChange` debounces those directly so we don't fire a duplicate request
	// every time the popover opens (which is what happened when both this and the old
	// debounce effect shared `isOpen` in their deps).
	useEffect(() => {
		if (!isOpen) return;
		setHighlighted(-1);
		runner.run(searchText);
		// `searchText` intentionally not a dep — edits go through `handleInputChange`.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, runner]);

	useEffect(() => {
		setHighlighted(options.length ? 0 : -1);
	}, [options]);

	useEffect(() => {
		if (autoFocus) {
			requestAnimationFrame(() => inputRef.current?.focus());
		}
	}, [autoFocus]);

	// Reset the popover and any in-flight / queued search work, but do NOT fire `onBlur`.
	// Used by paths that close the popover programmatically (Escape, selection) where
	// we don't want a focus-driven side effect on the consumer's form field.
	const resetPopoverState = () => {
		setIsOpen(false);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		runner.cancel();
		setOptions([]);
	};

	// Full close: reset state AND fire the consumer's `onBlur`. Called when the input
	// actually loses focus (via `handleInputBlur`) so form-level blur handlers run once.
	const handleClose = () => {
		resetPopoverState();
		onBlur?.();
	};

	const handleFocus = (_e: FocusEvent<HTMLInputElement>) => {
		if (isDisabled) return;
		setIsOpen(true);
	};

	// Close on blur unless focus moved into the popover content (clicking an option).
	// We can't rely on Chakra's `closeOnBlur` because the input is rendered outside the
	// PopoverTrigger, so Chakra wouldn't recognize the input as "the trigger" for its
	// blur logic.
	const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
		const next = e.relatedTarget as Node | null;
		if (next && popoverRef.current?.contains(next)) return;
		handleClose();
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const next = e.target.value;
		setSearchText(next);
		if (!isOpen) setIsOpen(true);

		// Debounce live as the user types. Cancelling the prior timeout per keystroke
		// means we only ever fire one request per "settled" search, and the open effect
		// above handles the initial pre-populated fetch separately.
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			runner.run(next);
		}, 300);
	};

	const handleSelect = (option: T) => {
		onChange(option);
		setSearchText('');
		resetPopoverState();
	};

	const handleClear = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onChange(null);
		setSearchText('');
		// Re-focus the input that just re-mounted so the user can immediately type a new query.
		requestAnimationFrame(() => inputRef.current?.focus());
	};

	const handleInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
		const currentOptions = optionsRef.current;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (!isOpen) {
				setIsOpen(true);
				return;
			}
			if (!currentOptions.length) return;
			const next = (highlightedRef.current + 1) % currentOptions.length;
			setHighlighted(next);
			return;
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (!currentOptions.length) return;
			const current = highlightedRef.current;
			const next = current <= 0 ? currentOptions.length - 1 : current - 1;
			setHighlighted(next);
			return;
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			const idx = highlightedRef.current;
			if (idx >= 0 && idx < currentOptions.length) {
				handleSelect(currentOptions[idx]);
			}
			return;
		}

		if (e.key === 'Escape') {
			if (!isOpen) return;
			e.preventDefault();
			// WAI-ARIA combobox: Esc closes the popup but keeps the textbox focused. Going
			// through `resetPopoverState` (not `handleClose`) intentionally skips firing
			// `onBlur` — dismissing the dropdown isn't the same as the field losing focus.
			resetPopoverState();
		}
	};

	const wrapperBorderColor = form.isInvalid ? 'error.inputBorder' : isOpen ? 'border.focus' : 'border.default';
	// Match the `T | null` contract precisely: `null` is the only "no selection" sentinel.
	// `!value` would also collapse `0`, `""`, and `false` into "no selection", which would
	// silently hide `renderTrigger` for callers that legitimately use those as values.
	const showInput = value === null;

	// Clicking anywhere on the wrapper padding should focus the (unstyled) input
	// so the entire visible "field" feels like one input.
	const handleWrapperMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		if (!showInput || isDisabled) return;
		if (e.target === inputRef.current) return;
		e.preventDefault();
		inputRef.current?.focus();
	};

	// The "field" (input or display card) is rendered OUTSIDE Chakra's `PopoverTrigger`
	// to avoid Chakra's chained click/blur handlers fighting with our own focus-driven
	// open state — that race was causing the popover to flicker open and immediately
	// close on first click. Mirrors `meetings-search-dropdown.tsx`'s pattern: a
	// zero-height invisible anchor inside `PopoverTrigger` is what Popper positions
	// against, and we manage open/close entirely through the input's focus/blur.
	return (
		<Box position="relative" w="full">
			<Box
				alignItems="center"
				aria-label={!showInput ? (ariaLabel ?? placeholder) : undefined}
				bg="bg.surface"
				border="1px solid"
				borderColor={wrapperBorderColor}
				borderRadius="md"
				boxShadow={isOpen ? '0 0 0 1px var(--chakra-colors-blue-500)' : form.isInvalid ? '0 0 0 1px var(--chakra-colors-red-500)' : undefined}
				cursor={isDisabled ? 'not-allowed' : showInput ? 'text' : 'default'}
				display="flex"
				gap={2}
				justifyContent="space-between"
				onMouseDown={handleWrapperMouseDown}
				opacity={isDisabled ? 0.6 : 1}
				px={3}
				py={2}
				w="full"
				_hover={isDisabled || isOpen || !showInput ? {} : { borderColor: 'border.focus' }}
				{...(form.isInvalid && { 'data-invalid': 'true' })}
				{...rest}
			>
				{showInput ? (
					<Input
						ref={inputRef}
						aria-activedescendant={highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
						aria-autocomplete="list"
						aria-controls={isOpen ? listId : undefined}
						aria-expanded={isOpen}
						aria-haspopup="listbox"
						aria-label={ariaLabel ?? placeholder}
						autoComplete="off"
						flex={1}
						fontSize="sm"
						h="auto"
						isDisabled={isDisabled}
						minW={0}
						onBlur={handleInputBlur}
						onChange={handleInputChange}
						onFocus={handleFocus}
						onKeyDown={handleInputKey}
						// Tiny left padding so the focus caret has breathing room at position 0;
						// without it the caret renders flush against the wrapper's padding edge
						// and gets visually clipped until the user types something.
						pl="2px"
						pr={0}
						py={0}
						placeholder={isOpen ? '' : placeholder}
						role="combobox"
						value={searchText}
						variant="unstyled"
						_placeholder={{ color: 'text.softest' }}
					/>
				) : (
					<>
						<Box flex={1} minW={0}>
							{renderTrigger(value)}
						</Box>
						{!isDisabled && (
							<Center
								as="button"
								type="button"
								aria-label={clearLabel ?? 'Clear selection'}
								borderRadius="full"
								color="text.softer"
								onClick={handleClear}
								p={1}
								_hover={{ bg: 'secondary.bgAlpha' }}
							>
								<XOutline size="xs" />
							</Center>
						)}
					</>
				)}
			</Box>

			<Popover autoFocus={false} closeOnBlur={false} closeOnEsc={false} isLazy isOpen={isOpen} placement="bottom-start">
				<PopoverTrigger>
					{/* Zero-height invisible anchor positioned at the bottom edge of the field
					    so the popover renders directly underneath. */}
					<Box position="absolute" top="100%" left={0} right={0} h={0} aria-hidden />
				</PopoverTrigger>

				<Portal>
					{/*
					 * Width behaviour mirrors the give-box's `SelectorBox`:
					 * `w="max-content"` — widest option drives the menu width,
					 * `minW="20rem"` — sensible floor (matches `SelectorBox`),
					 * `maxW="90vw"` — never overflow the viewport.
					 * `onMouseDown` preventDefault keeps the input focused while clicking
					 * options so the input doesn't blur and close the popover mid-click.
					 */}
					<PopoverContent ref={popoverRef} id={menuId} w="max-content" minW="20rem" maxW="90vw" onMouseDown={(e) => e.preventDefault()}>
						<PopoverBody p={1}>
							<Stack as="ul" id={listId} role="listbox" overflowY="auto" maxH="22rem" spacing={0}>
								{status === Status.Loading && options.length === 0 && (
									<Center py={4}>
										<Spinner size="sm" />
									</Center>
								)}

								{status === Status.Idle && options.length === 0 && emptyState && (
									<Box as="li" listStyleType="none">
										{emptyState}
									</Box>
								)}

								{options.map((option, index) => {
									const id = optionId(index);
									const isHighlighted = index === highlightedIndex;
									return (
										<Box as="li" key={getOptionKey(option)} listStyleType="none" w="full">
											<Box
												as="button"
												type="button"
												id={id}
												role="option"
												aria-selected={isHighlighted}
												bg={isHighlighted ? 'secondary.bgAlpha' : 'transparent'}
												borderRadius="sm"
												cursor="pointer"
												display="block"
												onClick={(e: MouseEvent) => {
													e.preventDefault();
													handleSelect(option);
												}}
												onMouseEnter={() => setHighlighted(index)}
												textAlign="left"
												transition="background-color 0.15s ease-in-out"
												w="full"
												_hover={{ bg: 'secondary.bgAlpha' }}
											>
												{renderOption(option, isHighlighted)}
											</Box>
										</Box>
									);
								})}
							</Stack>
						</PopoverBody>
					</PopoverContent>
				</Portal>
			</Popover>
		</Box>
	);
};
